import MediaDeviceSelector from "../MediaDeviceSelector";
import LogoIcon from '../general/InpritonLogoHeader.svg?react'
import LangIcon from '../general/IconHeaderLang.svg?react'
import LanguageSwitch from "../general/LanguageSwitch";

import './CheckIn.scss'
import {useTranslation} from "react-i18next";
import React, {createRef, useContext, useEffect, useRef, useState} from "react";
import AppContext from "../../context/AppContext";
import {ConferenceJoinResponse, PushNotificationSubscription, TranslationConferenceTokenData} from "@inpriton/types";
import {jwtDecode} from "jwt-decode";
import {usePushNotificationService} from "../../lib/pushNotification/PushNotificationService";
import NotificationContext, {NotificationList} from "../../context/NotificationContext";
import {API_URL} from "../../config";
import {savePreferences, PreferencesBody, withSavedLanguages} from "../../lib/preferences";
import {getLanguageName} from "../../lib/languageNames";
import infoIcon from "../icons/svg/info.svg";
import dropdownChevron from "../icons/svg/dropdown-chevron.svg";
import checkboxIcon from "../icons/svg/checkbox.svg";
import MediaContext from "../../context/MediaContext";
import useWebRtcClient from "../../webrtc/WebRtcClient";
import {useNavigate, useParams} from "react-router-dom";
import {storeJoinTicket} from "../../lib/joinTicket";

export default () => {
  const {t} = useTranslation()
  const { createNotification, hideNotification } = useContext(NotificationContext)
  const notificationService = usePushNotificationService()
  const webrtc = useWebRtcClient()
  const navigate = useNavigate()

  const {token} = useParams<{ token: string }>()

  const {
    initialize,
    initialized,
    checkedIn,
    keys,
    ws,
    setUsers,
    setUserData,
    setCurrentRoom,
    setConferenceStatus,
    setCheckedIn,
    userData,
    updateStreams,
    onHistory,
  } = useContext(AppContext)

  const {
    publishLocalMedia,
  } = useContext(MediaContext)

  const nameRef = createRef<HTMLInputElement>()

  const [name, setName] = useState<string>(() => {
    try {
      const user = jwtDecode<TranslationConferenceTokenData>(token as string).user
      return user?.name || ''
    } catch {
      return ''
    }
  })
  const [permissionDenied] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)
  const [roomInfo, setRoomInfo] = useState<any>(null)
  const [roomLoaded, setRoomLoaded] = useState(false)

  const [spokenLanguage, setSpokenLanguage] = useState<string>('')
  const [neededLanguages, setNeededLanguages] = useState<string[]>([])
  const [simultanConfig, setSimultanConfig] = useState<number | null>(null)
  const [spokenLangOpen, setSpokenLangOpen] = useState(false)
  const spokenLangRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (spokenLangRef.current && !spokenLangRef.current.contains(e.target as Node)) {
        setSpokenLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    initialize(token as string)

    fetch(`${API_URL}/conference/room`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setRoomInfo(data))
      .catch(e => console.error('Failed to fetch room info', e))
      .finally(() => setRoomLoaded(true))
  }, []);

  // Decode token to check user role
  const tokenUser: TranslationConferenceTokenData['user'] | null = (() => {
    try { return jwtDecode<TranslationConferenceTokenData>(token as string).user } catch { return null }
  })()

  const isAttendee = tokenUser ? !tokenUser.isTranslator : false

  const allLanguages: string[] = (() => {
    try { return JSON.parse(roomInfo?.room?.meta?.['general/conferenceLanguages'] || '[]') } catch { return [] }
  })()

  const isSim = roomInfo?.room?.meta?.['general/translationType'] === '2'

  const showPrefs = isAttendee && allLanguages.length > 0

  const toggleNeeded = (lang: string) => {
    setNeededLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    )
    if (spokenLanguage === lang) setSpokenLanguage('')
  }

  const handleSpokenLanguage = (lang: string) => {
    setSpokenLanguage(lang)
    setNeededLanguages(prev => prev.filter(l => l !== lang))
  }

  const joinDisabled = permissionDenied || loggingIn || !name ||
    (showPrefs && isSim && neededLanguages.length > 0 && !simultanConfig)

  const onUsernameFieldKeyUp = (e: any) => {
    // Not covered by the field's own `disabled`, which omits `!name` — Enter would join blank.
    if ((e as KeyboardEvent).key === 'Enter' && !joinDisabled) {
      onJoinClicked(e)
    }
  }

  // Wait for the /conference/room result before deciding what to render, so a removed user
  // never flashes the join form for a frame before the notice (and language prefs don't pop
  // in for everyone else). `.finally` above flips roomLoaded even if the fetch errors, so a
  // failed room lookup falls through to the lobby rather than hanging on "loading".
  if (!initialized || !roomLoaded) {
    return (
      <div className="check-in-loading">
        <span className="material-symbols-sharp">progress_activity</span>
      </div>
    )
  }

  // A dead link: show a terminal notice instead of a join form conference:join would reject.
  // Keep the two reasons apart — only a kick may say the host removed you (RISKS.md/P1.21).
  const deadLink = (roomInfo?.removed || roomInfo?.error === 'removed')
    ? { title: 'chat.removed.title', message: 'chat.removed.message' }
    : roomInfo?.code === 'session_revoked'
      ? { title: 'chat.sessionExpired.title', message: 'chat.sessionExpired.joinMessage' }
      : null

  if (deadLink) {
    return (
      <div className="check-in">
        <header>
          <div className="frame-35">
            <div className="logo">
              <LogoIcon/>
              <span className="logo-text">inpriton</span>
            </div>
            <div className="icon-header-lang">
              <LangIcon/>
              <LanguageSwitch/>
            </div>
          </div>
        </header>

        <div className="removed-notice">
          <h2 className="removed-notice__title">{t(deadLink.title)}</h2>
          <p className="removed-notice__message">{t(deadLink.message)}</p>
        </div>
      </div>
    )
  }

  const onJoinClicked = (e: any) => {
    e.preventDefault()

    if (!ws) {
      throw new Error('no websocket client')
    }

    if (!keys) {
      throw new Error('no keys')
    }

    setLoggingIn(true)

    // RISKS.md/P1.22: repeat only what the NEW socket lost — the join, our published media (the
    // server dropped its transports with the old socket) and the consumers (`webrtc.reset()` closed
    // them). Never push registration, preferences or navigation: those belong to the first join.
    const onReconnected = async () => {
      try {
        const response = await ws.joinConference(name, keys.publicKey, null)
        const confStatus = response.conference

        // Merge, don't replace: the server sends userPreferences: null to a user who has saved no
        // languages, and an empty assignedTranslatorUuids mutes every interpreter
        // (VideoGrid.isAutoMuted).
        setConferenceStatus(prev => ({
          ...confStatus,
          userPreferences: confStatus.userPreferences ?? prev.userPreferences,
        }))
        setUsers(confStatus.users)
        onHistory(response.history ?? [], { reconnect: true, publicKey: keys.publicKey })

        await publishLocalMedia()

        await Promise.all(confStatus.users.map(user => updateStreams(user.uuid, user.status, true)))
      } catch (e: any) {
        // Only `unauthenticated` is terminal. Without the forced reconnect the socket stays open and
        // `wsConnected` — no overlay, nothing retrying — and the server accepts nothing without a token.
        if (e?.cause !== 'unauthenticated') {
          console.warn('re-join failed, reconnecting', e)
          ws.forceReconnect()
          return
        }
        console.error('re-join rejected', e)
        ws.sessionDead(e?.message === 'user removed' ? 'removed' : 'revoked')
      }
    }

    ws.connect(async () => {
      let subscription: PushNotificationSubscription|null = null

      try {
        subscription = await notificationService.register()
      } catch (e: any) {
        console.warn(e)
      }

      await ws.joinConference(name, keys.publicKey, subscription)
        .then(async (response: ConferenceJoinResponse) => {
          const tokenData = jwtDecode(ws.token as string) as TranslationConferenceTokenData
          const confStatus = response.conference

          setConferenceStatus(confStatus)
          setUsers(confStatus.users)
          onHistory(response.history ?? [], { reconnect: false, publicKey: keys.publicKey })
          setCheckedIn(true)
          setLoggingIn(false)

          console.log('[CheckIn] admitted:', confStatus.admitted, 'isTranslator:', tokenData.user.isTranslator)
          if (!confStatus.admitted) {
            console.log('[CheckIn] creating admitted-waiting notification')
            createNotification({
              id: 'admitted-waiting',
              message: t('chat.waitToEnter'),
              persistent: true,
            })
          }

          webrtc.setRoom(tokenData.conference)

          await publishLocalMedia()

          try {
            await Promise.all(confStatus.users.map(user => {
              return updateStreams(user.uuid, user.status, true)
            }))
          } catch (e) {
            console.error(e)
          }

          if (showPrefs) {
            try {
              const body: PreferencesBody = { spokenLanguage, neededLanguages }
              if (isSim && simultanConfig) body.simultanConfig = simultanConfig
              // RISKS.md/P1.20: savePreferences throws on a non-2xx, so everything below runs only
              // on a real success — a rejected response must never be coerced into EMPTY assigned
              // translators.
              await savePreferences(ws, body)
              setConferenceStatus(prev => ({
                ...prev,
                userPreferences: withSavedLanguages(prev.userPreferences, { spokenLanguage, neededLanguages, simultanConfig }),
              }))
            } catch (e) {
              // The join itself already succeeded, so continue into the meeting — but say so, rather
              // than arriving with silently empty preferences.
              console.error('Failed to save preferences', e)
              createNotification({
                id: 'prefs-save-failed',
                // Join-flow wording: this screen offers no retry, so it names where the settings can
                // be changed instead (the sidebar uses the plain "try again" copy).
                message: t('chat.prefsSaveFailedJoin'),
                // Persistent, not ttl'd: the preferences stay unsaved until the user acts, so this
                // is dismissed by a later successful save (SidebarDeviceSettings), not by a clock.
                persistent: true,
              })
            }
          }

          // A previous attempt's persistent toast outlives this view — it would report a failure
          // into the meeting room after the retry succeeded.
          hideNotification('join-error')
          // RISKS.md/P2.79: stored at the join, not when the green room opens: a link opened in a
          // meeting tab and never joined, a refused one included, must not replace the joined
          // session's ticket.
          storeJoinTicket(token as string)
          navigate('/meeting', { replace: true })
        })
        .catch(async e => {
          console.error(e)
          setLoggingIn(false)
          // RISKS.md/P1.19 + P1.20: `conference:join` rejects with cause 'unauthenticated' for BOTH
          // a kick and an ordinary revoked/expired session, so branch on the server's message
          // ('user removed' vs 'session revoked' — WsClient rejects with error.message). Collapsing
          // these accuses the host of removing a user whose session merely lapsed, and tells them to
          // give up rather than request a new link. Anything else stays generic.
          const message = e?.message === 'user removed'
            ? t('chat.removed.message')
            : (e?.message === 'session revoked' ? t('chat.sessionExpired.joinMessage') : t('chat.joinFailed'))
          createNotification({
            id: 'join-error',
            message,
            persistent: true,
          })
        })
    }, onReconnected)
  }

  return (
    <div className="check-in">
      <header>
        <div className="frame-35">
          <div className="logo">
            <LogoIcon/>
            <span className="logo-text">inpriton</span>
          </div>
          <div className="icon-header-lang">
            <LangIcon/>
            <LanguageSwitch/>
          </div>
        </div>
      </header>

      <div className="lobby">
        <div className="inner">
          <div className="left">
            <div className="inner">
              <MediaDeviceSelector/>
            </div>
          </div>

          <div className="right">
            <div className="inner">
              <div className="box data figma-card">
                <div className="card-frame">
                <h2 style={{
                  alignSelf: 'stretch',
                  color: '#000',
                  fontFamily: 'var(--font-family-title, Roboto)',
                  fontSize: 'var(--font-size-3xl, 28px)',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'var(--font-line-height-xl, 40px)',
                  margin: 0,
                }}>
                  {t('lobby.yourName')}
                </h2>

                <div className="card-fields">
                <div className="card-group">
                <div className="input-field">
                  <div className="input-label-row">
                    <label htmlFor="name" className="input-label">{t('lobby.nameLabel')}</label>
                  </div>
                  <div className="input-text-field">
                    <div className="input-inner">
                      <input
                        type="text"
                        id="name"
                        ref={nameRef}
                        value={name}
                        disabled={permissionDenied || loggingIn}
                        onChange={e => setName(e.target.value)}
                        onKeyUp={onUsernameFieldKeyUp}
                        className="input-text"
                      />
                    </div>
                  </div>
                </div>

              {showPrefs && (
                <>
                  <div className="spoken-language">
                    <div className="text-with-info">
                      <span className="field-label">{t('conferenceInfo.spokenLanguage')}</span>
                      <div className="info-icon info-icon--tooltip">
                        <img src={infoIcon} alt="" aria-hidden="true"/>
                        <div className="info-tooltip">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6" fill="none">
                            <path d="M6.92773 0L13.8559 6H-0.000468731L6.92773 0Z" fill="#212629"/>
                          </svg>
                          <div className="info-tooltip__frame">
                            {t('lobby.spokenLanguageInfo')}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`dropdown-input${spokenLangOpen ? ' dropdown-input--open' : ''}`}
                      ref={spokenLangRef}
                      onClick={() => setSpokenLangOpen(o => !o)}
                    >
                      <span className={`dropdown-value${!spokenLanguage ? ' dropdown-value--placeholder' : ''}`}>
                        {spokenLanguage ? getLanguageName(spokenLanguage) : t('conferenceInfo.selectPlaceholder')}
                      </span>
                      <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                      {spokenLangOpen && (
                        <div className="dropdown-options">
                          <div
                            className="dropdown-option"
                            onClick={e => { e.stopPropagation(); setSpokenLanguage(''); setSpokenLangOpen(false) }}
                          >
                            {t('conferenceInfo.selectPlaceholder')}
                          </div>
                          {allLanguages.filter(l => !neededLanguages.includes(l)).map(lang => (
                            <div
                              key={lang}
                              className={`dropdown-option${spokenLanguage === lang ? ' dropdown-option--selected' : ''}`}
                              onClick={e => { e.stopPropagation(); handleSpokenLanguage(lang); setSpokenLangOpen(false) }}
                            >
                              {getLanguageName(lang)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="needed-languages">
                    <span className="field-label">{t('conferenceInfo.neededLanguages')}</span>
                    <div className="lang-chips">
                      {allLanguages.filter(l => l !== spokenLanguage).map(lang => (
                        <button
                          key={lang}
                          type="button"
                          className={`lang-chip${neededLanguages.includes(lang) ? ' lang-chip--active' : ''}`}
                          onClick={() => toggleNeeded(lang)}
                        >
                          {getLanguageName(lang)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isSim && neededLanguages.length > 0 && (
                    <div className="simutan-section">
                      <div className="text-with-info">
                        <span className="field-label">{t('conferenceInfo.simultanConfig')}</span>
                        <div className="info-icon info-icon--tooltip">
                          <img src={infoIcon} alt="" aria-hidden="true"/>
                          <div className="info-tooltip info-tooltip--wide">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6" fill="none">
                              <path d="M6.92773 0L13.8559 6H-0.000468731L6.92773 0Z" fill="#212629"/>
                            </svg>
                            <div className="info-tooltip__frame">
                              {t('lobby.simultanConfigInfo')}
                            </div>
                          </div>
                        </div>
                      </div>
                      <label className="simutan-radio-label">
                        <input
                          type="radio"
                          name="simultanConfig"
                          checked={simultanConfig === 1}
                          onChange={() => setSimultanConfig(1)}
                          className="simutan-radio-input"
                        />
                        <span className="simutan-radio-icon">
                          {simultanConfig === 1
                            ? <img src={checkboxIcon} alt="" aria-hidden="true"/>
                            : <span className="simutan-radio-empty"/>
                          }
                        </span>
                        {t('conferenceInfo.simultanReduce')}
                      </label>
                      <label className="simutan-radio-label">
                        <input
                          type="radio"
                          name="simultanConfig"
                          checked={simultanConfig === 2}
                          onChange={() => setSimultanConfig(2)}
                          className="simutan-radio-input"
                        />
                        <span className="simutan-radio-icon">
                          {simultanConfig === 2
                            ? <img src={checkboxIcon} alt="" aria-hidden="true"/>
                            : <span className="simutan-radio-empty"/>
                          }
                        </span>
                        {t('conferenceInfo.simultanMute')}
                      </label>
                    </div>
                  )}
                </>
              )}
                <button
                  className="join-button"
                  disabled={joinDisabled}
                  onClick={onJoinClicked}
                >
                  {t('lobby.enterNow')}
                </button>
                </div>{/* card-group */}
                </div>{/* card-fields */}
                </div>{/* card-frame */}
              </div>{/* figma-card */}

            </div>
          </div>
        </div>
        <p className="agb-text">
          <span className="agb-text__grey">{t('lobby.agb.prefix')} </span>
          <span className="agb-text__link">{t('lobby.agb.terms')}</span>
          <span className="agb-text__grey"> {t('lobby.agb.conjunction')} </span>
          <span className="agb-text__link">{t('lobby.agb.privacy')}</span>
          <span className="agb-text__grey"> {t('lobby.agb.suffix')}</span>
        </p>
      </div>
      {/* This list WHITELISTS ids, so a toast whose id is missing here renders nowhere (RISKS.md/
          P1.14). Device failures belong on this screen — it owns the preview. Meeting-room notices
          (admit requests, "waiting to be admitted") are deliberately excluded. */}
      <NotificationList filter={n => [
        'join-error',
        'prefs-save-failed',
        'camera-denied',
        'camera-unavailable',
        'camera-fallback',
        'device-list-failed',
        'microphone-denied',
        'microphone-unavailable',
        'microphone-fallback',
        'microphone-muted-system',
        'speaker-fallback',
        'speaker-unavailable',
        'speaker-blocked',
        'speaker-not-found',
        'speaker-test',
        'speaker-test-failed',
        'mic-test',
        'mic-test-failed',
      ].includes(n.id)}/>

    </div>
  )
}
